import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  AutoAwesome as AutoAwesomeIcon,
  Language as LanguageIcon,
} from "@mui/icons-material";
import api, { generateFoodImage, fillNutritionWithAI } from "./api";
import { processImageFile, validateImageFile } from "./utils/imageUtils";
import { AVAILABLE_LANGUAGES } from "./utils/languages";
import TagSelector from "./components/TagSelector";

export default function AddFoodForm() {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);
  const [addLanguageDialog, setAddLanguageDialog] = useState(false);
  const [newLanguageName, setNewLanguageName] = useState("");
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imageGenerating, setImageGenerating] = useState(false);
  const [nutritionLoading, setNutritionLoading] = useState(false);
  const [isLongLoading, setIsLongLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [selectedTagIds, setSelectedTagIds] = useState([]);

  // Default languages - moved to component level for easier future modification
  const defaultLanguages = AVAILABLE_LANGUAGES;

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    categoryId: "",
    isCustom: false,
    showOriginalAmount: false,
    generateImage: false,
    calories: "",
    protein: "",
    fat: "",
    carbs: "",
    glycemicIndex: "",
    unitType: "g",
    unitValue: "100",
    portionSize: "",
    languageSpec: {
      ...defaultLanguages.reduce((acc, lang) => {
        acc[lang] = { name: "", description: "", customImageUrl: "" };
        return acc;
      }, {})
    },
    image: "",
    imageUploading: false,
  });

  // Function to fetch categories from backend
  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      // Get all categories
      const response = await api.get(`/category/categories`);
      if (response.data.success) {
        setCategories(response.data.data);
      } else {
        showSnackbar("Failed to fetch categories", "error");
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
      showSnackbar("Failed to load categories. Please try again later.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch categories when component mounts
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Add this useEffect after the other useEffects
  useEffect(() => {
    let timeoutId;
    if (nutritionLoading) {
      timeoutId = setTimeout(() => {
        setIsLongLoading(true);
      }, 10000); // 10 seconds
    } else {
      setIsLongLoading(false);
    }
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [nutritionLoading]);

  // Handle input change for general fields
  const handleInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  // Handle language specific change
  const handleLanguageChange = (language, field, value) => {
    setFormData({
      ...formData,
      languageSpec: {
        ...formData.languageSpec,
        [language]: {
          ...formData.languageSpec[language],
          [field]: value,
        },
      },
    });

    // If it's the English name and the main name is empty, use it as the main name too
    if (language === "english" && field === "name" && !formData.name) {
      handleInputChange("name", value);
    }
  };

  // Handle main image upload (improved)
  const handleMainImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Start loading state for image upload
    setFormData(prev => ({
      ...prev,
      imageUploading: true
    }));

    try {
      // Validate and process image
      validateImageFile(file);
      const processedFile = await processImageFile(file);
      
      const formDataObj = new FormData();
      formDataObj.append("image", processedFile);

      const response = await api.post('/upload-image', formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data && response.data.success && response.data.url) {
        handleInputChange('image', response.data.url);
        showSnackbar(`Image uploaded successfully! (${Math.round(processedFile.size / 1024)}KB, 200x200px)`, "success");
      } else {
        throw new Error('Failed to upload image');
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      showSnackbar(error.message || "Failed to upload image. Please try again.", "error");
    } finally {
      setFormData(prev => ({
        ...prev,
        imageUploading: false
      }));
    }
  };

  // Handle image upload for a language (improved)
  const handleLanguageImageUpload = async (language, e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Set loading state for this specific image
    setFormData((prev) => ({
      ...prev,
      languageSpec: {
        ...prev.languageSpec,
        [language]: {
          ...prev.languageSpec[language],
          uploading: true,
        },
      },
    }));

    try {
      // Validate and process image
      validateImageFile(file);
      const processedFile = await processImageFile(file);
      
      const formDataObj = new FormData();
      formDataObj.append("image", processedFile);

      const response = await api.post('/upload-image', formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data && response.data.success && response.data.url) {
        const imageUrl = response.data.url;
        handleLanguageChange(language, "customImageUrl", imageUrl);
        
        // If this is the first image, use it as the main image
        if (!formData.image) {
          handleInputChange("image", imageUrl);
        }
        
        showSnackbar(`${language} image uploaded successfully! (${Math.round(processedFile.size / 1024)}KB, 200x200px)`, "success");
      } else {
        throw new Error('Failed to upload image');
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      showSnackbar(error.message || `Failed to upload ${language} image`, "error");
    } finally {
      // Clear loading state
      setFormData((prev) => ({
        ...prev,
        languageSpec: {
          ...prev.languageSpec,
          [language]: {
            ...prev.languageSpec[language],
            uploading: false,
          },
        },
      }));
    }
  };

  // Handle tab change for languages
  const handleTabChange = (_, newValue) => {
    if (newValue === Object.keys(formData.languageSpec).length) {
      // "+" tab was clicked
      setAddLanguageDialog(true);
    } else {
      setCurrentTab(newValue);
    }
  };

  // Handle adding a new language
  const handleAddLanguage = () => {
    if (
      newLanguageName &&
      !Object.keys(formData.languageSpec).includes(
        newLanguageName.toLowerCase()
      )
    ) {
      const language = newLanguageName.toLowerCase();

      setFormData({
        ...formData,
        languageSpec: {
          ...formData.languageSpec,
          [language]: { name: "", description: "", customImageUrl: "" },
        },
      });

      setNewLanguageName("");
      setAddLanguageDialog(false);

      // Switch to the new language tab
      setCurrentTab(Object.keys(formData.languageSpec).length);
    }
  };

  // Handle removing a language
  const handleRemoveLanguage = (language) => {
    if (defaultLanguages.includes(language)) {
      showSnackbar(`Cannot remove default language: ${language}`, "error");
      return;
    }

    const updatedLanguageSpec = { ...formData.languageSpec };
    delete updatedLanguageSpec[language];

    setFormData({
      ...formData,
      languageSpec: updatedLanguageSpec,
    });

    // Reset tab if needed
    if (currentTab >= Object.keys(updatedLanguageSpec).length) {
      setCurrentTab(0);
    }
  };

  // Validate form before submission
  const validateForm = () => {
    if (!formData.name.trim()) {
      showSnackbar("Food name is required", "error");
      return false;
    }
    if (!formData.categoryId) {
      showSnackbar("Category is required", "error");
      return false;
    }
    return true;
  };

  // Show snackbar notification
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Prepare data for API
  const prepareDataForApi = () => {
    // Find the selected category name
    const selectedCategory = formData.categoryId ? 
      categories.find(cat => cat.id === parseInt(formData.categoryId)) : null;
    
    // Convert string values to appropriate types
    const apiData = {
      ...formData,
      category: selectedCategory ? selectedCategory.name : null, // Add category name
      categoryId: formData.categoryId ? parseInt(formData.categoryId) : null,
      calories: formData.calories ? parseFloat(formData.calories) : null,
      protein: formData.protein ? parseFloat(formData.protein) : null,
      fat: formData.fat ? parseFloat(formData.fat) : null,
      carbs: formData.carbs ? parseFloat(formData.carbs) : null,
      glycemicIndex: formData.glycemicIndex
        ? parseFloat(formData.glycemicIndex)
        : null,
      portionSize: formData.portionSize ? parseFloat(formData.portionSize) : null,
      showOriginalAmount: formData.showOriginalAmount || false,
    };

    return apiData;
  };

  // Generate AI image only
  const handleGenerateImage = async () => {
    if (!formData.name.trim()) {
      showSnackbar("Please enter a food name first", "error");
      return;
    }

    setImageGenerating(true);
    try {
      const selectedCategory = formData.categoryId ? 
        categories.find(cat => cat.id === parseInt(formData.categoryId)) : null;
      const categoryName = selectedCategory ? selectedCategory.name : null;

      const response = await generateFoodImage(formData.name, categoryName);

      if (response.success) {
        setFormData(prev => ({
          ...prev,
          image: response.data.imageUrl
        }));
        showSnackbar("AI image generated successfully!", "success");
      } else {
        throw new Error(response.error || "Failed to generate image");
      }
    } catch (error) {
      console.error("Error generating image:", error);
      showSnackbar(error.message || "Failed to generate image", "error");
    } finally {
      setImageGenerating(false);
    }
  };

  // Fill nutritional details with AI (without image)
  const handleFillNutrition = async () => {
    if (!formData.name.trim()) {
      showSnackbar("Please enter a food name first", "error");
      return;
    }

    setNutritionLoading(true);
    try {
      const selectedCategory = formData.categoryId ? 
        categories.find(cat => cat.id === parseInt(formData.categoryId)) : null;
      const categoryName = selectedCategory ? selectedCategory.name : null;

      const response = await fillNutritionWithAI(
        formData.name, 
        categoryName, 
        Object.keys(formData.languageSpec)
      );

      if (response.success) {
        const aiData = response.data;
        
        setFormData(prev => ({
          ...prev,
          calories: aiData.calories?.toString() || prev.calories,
          protein: aiData.protein?.toString() || prev.protein,
          fat: aiData.fat?.toString() || prev.fat,
          carbs: aiData.carbs?.toString() || prev.carbs,
          glycemicIndex: aiData.glycemicIndex?.toString() || prev.glycemicIndex,
          languageSpec: {
            ...prev.languageSpec,
            ...aiData.languageSpec
          }
        }));

        showSnackbar("Nutritional details filled successfully with AI!", "success");
      } else {
        throw new Error(response.error || "Failed to get AI analysis");
      }
    } catch (error) {
      console.error("Error filling nutrition:", error);
      showSnackbar(error.message || "Failed to fill nutrition with AI", "error");
    } finally {
      setNutritionLoading(false);
    }
  };

  // Save the new food
  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const apiData = prepareDataForApi();

      // Call the backend API to create food item
      const response = await api.post(`/foods`, apiData, {
        headers: {
          "Content-Type": "application/json",
          // 'Authorization': `Bearer ${localStorage.getItem('token')}` // Add token if using JWT
        },
      });

      if (response.data.success) {
        const foodId = response.data.data.id;
        
        // Assign tags if any were selected
        if (selectedTagIds.length > 0) {
          try {
            await api.post(`/tags/food/${foodId}`, { tagIds: selectedTagIds });
          } catch (tagError) {
            console.error("Error assigning tags:", tagError);
            // Don't fail the whole operation if tags fail
          }
        }
        
        showSnackbar(`Food item "${formData.name}" created successfully!`);
        // Navigate to the food detail page
        setTimeout(() => {
          navigate(`/food-detail/${foodId}`);
        }, 1500);
      } else {
        throw new Error(response.data.error || "Failed to create food item");
      }
    } catch (error) {
      console.error("Error creating food:", error);
      showSnackbar(error.message || "Failed to create food item", "error");
    } finally {
      setSaving(false);
    }
  };

  // Get first letter for avatar placeholder
  const getInitial = () => {
    return formData.name ? formData.name.charAt(0).toUpperCase() : "?";
  };

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: '#f8fafc', py: 3 }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, sm: 3, md: 4 } }}>
        {/* Header */}
        <Box sx={{ 
          mb: 4, 
          pb: 3,
          borderBottom: '2px solid #e2e8f0'
        }}>
          <Typography variant="h3" sx={{ 
            fontWeight: 'bold', 
            color: '#1e293b',
            mb: 1,
            fontSize: { xs: '1.8rem', md: '2.2rem' }
          }}>
            🍽️ Add New Food Item
          </Typography>
          <Typography variant="h6" sx={{ 
            color: '#64748b', 
            fontWeight: 'normal'
          }}>
            Create a new food entry with complete nutritional information
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Left Column - Food Preview & Basic Info */}
          <Grid item xs={12} lg={4}>
            {/* Food Preview Card */}
            <Paper elevation={3} sx={{ p: 3, mb: 3, backgroundColor: 'white', borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>
                📱 Food Preview
              </Typography>
              
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                {formData.image ? (
                  <Box sx={{ 
                    width: 150, 
                    height: 150, 
                    mx: 'auto',
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: '3px solid #e2e8f0'
                  }}>
                    <img
                      src={formData.image}
                      alt={formData.name || "Food preview"}
                      style={{ 
                        width: '100%', 
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </Box>
                ) : (
                  <Box sx={{ 
                    width: 150, 
                    height: 150, 
                    mx: 'auto',
                    borderRadius: 3,
                    backgroundColor: '#3b82f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '4rem',
                    fontWeight: 'bold',
                    border: '3px solid #e2e8f0'
                  }}>
                    {getInitial()}
                  </Box>
                )}
              </Box>

              <Typography variant="h5" sx={{ 
                fontWeight: 'bold', 
                textAlign: 'center',
                color: '#1e293b',
                mb: 1
              }}>
                {formData.name || "Food Name"}
              </Typography>
              
              <Typography variant="body2" sx={{ 
                textAlign: 'center',
                color: '#64748b',
                mb: 1
              }}>
                {categories.find(cat => cat.id === parseInt(formData.categoryId))?.name || "Select Category"}
              </Typography>

              {/* Description Preview */}
              {formData.description && (
                <Typography variant="body2" sx={{ 
                  textAlign: 'center',
                  color: '#475569',
                  mb: 2,
                  px: 2,
                  fontStyle: 'italic',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: 1.5
                }}>
                  "{formData.description}"
                </Typography>
              )}

              {/* Quick Stats */}
              {(formData.calories || formData.protein || formData.carbs || formData.fat) && (
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: 1,
                  mt: 2,
                  p: 2,
                  backgroundColor: '#f1f5f9',
                  borderRadius: 2
                }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="primary.main">{formData.calories || "0"}</Typography>
                    <Typography variant="caption" color="text.secondary">Calories</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="primary.main">{formData.protein || "0"}g</Typography>
                    <Typography variant="caption" color="text.secondary">Protein</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="primary.main">{formData.carbs || "0"}g</Typography>
                    <Typography variant="caption" color="text.secondary">Carbs</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="primary.main">{formData.fat || "0"}g</Typography>
                    <Typography variant="caption" color="text.secondary">Fat</Typography>
                  </Box>
                </Box>
              )}
            </Paper>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving || !formData.name || !formData.categoryId}
                size="large"
                fullWidth
                sx={{ 
                  height: '56px',
                  fontWeight: 'bold'
                }}
              >
                {saving ? "Saving..." : "💾 Save Food Item"}
              </Button>
            </Box>
          </Grid>

          {/* Right Column - Form Fields */}
          <Grid item xs={12} lg={8}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

              {/* Basic Information Section */}
              <Paper elevation={3} sx={{ p: 3, backgroundColor: 'white', borderRadius: 3 }}>
                <Typography variant="h6" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>
                  📝 Basic Information
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Food Name"
                      value={formData.name}
                      onChange={(e) => handleInputChange("name", e.target.value)}
                      placeholder="e.g., Organic Apple, Grilled Chicken..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={formData.categoryId}
                        label="Category"
                        onChange={(e) => handleInputChange("categoryId", e.target.value)}
                        disabled={loading}
                        sx={{
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                          }
                        }}
                      >
                        <MenuItem value="">
                          <em>Select a category</em>
                        </MenuItem>
                        {categories.map((category) => (
                          <MenuItem key={category.id} value={category.id}>
                            {category.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Unit Type"
                      value={formData.unitType}
                      onChange={(e) => handleInputChange("unitType", e.target.value)}
                      placeholder="g, ml, piece, cup..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Unit Value"
                      value={formData.unitValue}
                      onChange={(e) => handleInputChange("unitValue", e.target.value)}
                      placeholder="100"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                  
                  {/* Portion Size field - only show for piece/portion unit types */}
                  {(formData.unitType.toLowerCase().includes('piece') || 
                    formData.unitType.toLowerCase().includes('portion')) && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Portion Size (grams)"
                        value={formData.portionSize}
                        onChange={(e) => handleInputChange("portionSize", e.target.value)}
                        placeholder="e.g., 50 for 1 cookie = 50g"
                        helperText="Weight in grams of 1 piece/portion"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                      />
                    </Grid>
                  )}
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Description"
                      value={formData.description}
                      onChange={(e) => handleInputChange("description", e.target.value)}
                      placeholder="Enter a description of this food item..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TagSelector
                      selectedTagIds={selectedTagIds}
                      onTagsChange={setSelectedTagIds}
                      label="Select tags for this food"
                      placeholder="Choose relevant tags to categorize this food..."
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.showOriginalAmount || false}
                          onChange={(e) => handleInputChange("showOriginalAmount", e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Show Original Amount (bypass carb preference scaling)"
                      sx={{ 
                        mt: 1,
                        '& .MuiFormControlLabel-label': {
                          fontSize: '0.875rem',
                          color: 'text.secondary'
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Image Upload Section */}
              <Paper elevation={3} sx={{ p: 3, backgroundColor: 'white', borderRadius: 3 }}>
                <Typography variant="h6" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>
                  📷 Image Settings
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Image URL"
                      value={formData.image}
                      onChange={(e) => handleInputChange("image", e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      helperText="Paste URL or use upload button below"
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                    
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      disabled={formData.imageUploading}
                      sx={{ 
                        height: '56px',
                        borderRadius: 2,
                        borderStyle: 'dashed',
                        borderWidth: 2,
                        '&:hover': {
                          borderStyle: 'dashed',
                          borderWidth: 2,
                        }
                      }}
                    >
                      {formData.imageUploading ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <AddIcon sx={{ mr: 1 }} />
                          Upload Food Image
                        </>
                      )}
                      <input
                        type="file"
                        hidden
                        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                        onChange={handleMainImageUpload}
                      />
                    </Button>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ 
                      p: 3, 
                      backgroundColor: '#f8fafc', 
                      borderRadius: 2,
                      border: '1px solid #e2e8f0'
                    }}>
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<AutoAwesomeIcon />}
                        onClick={handleGenerateImage}
                        disabled={imageGenerating || !formData.name}
                        fullWidth
                        sx={{ 
                          height: '48px',
                          fontWeight: 'bold',
                          mb: 2
                        }}
                      >
                        {imageGenerating ? "🧠 Generating..." : "🧠 Generate AI Image"}
                      </Button>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        AI will create a realistic food image with plain background
                      </Typography>
                      
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2, p: 2, backgroundColor: 'white', borderRadius: 1 }}>
                        <strong>Note:</strong> Images are automatically resized to 200x200px and compressed to &lt;100KB
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Nutritional Information Section */}
              <Paper elevation={3} sx={{ p: 3, backgroundColor: 'white', borderRadius: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: isLongLoading ? 1 : 3 }}>
                  <Box>
                    <Typography variant="h6" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>
                      🥗 Nutritional Information
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Enter nutritional values per {formData.unitValue || "100"}{formData.unitType || "g"} serving
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={handleFillNutrition}
                    disabled={nutritionLoading || !formData.name}
                    sx={{ 
                      height: '48px',
                      fontWeight: 'bold',
                      minWidth: '180px'
                    }}
                  >
                    {nutritionLoading ? "🧠 Analyzing..." : "🧠 Fill with AI"}
                  </Button>
                </Box>
                {isLongLoading && nutritionLoading && (
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      textAlign: 'right', 
                      mb: 2,
                      fontStyle: 'italic'
                    }}
                  >
                    ⏳ This might take a minute as we analyze the food details...
                  </Typography>
                )}
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Calories"
                      value={formData.calories}
                      onChange={(e) => handleInputChange("calories", e.target.value)}
                      InputProps={{
                        inputProps: { step: "0.01", min: "0" }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Protein (g)"
                      value={formData.protein}
                      onChange={(e) => handleInputChange("protein", e.target.value)}
                      InputProps={{
                        inputProps: { step: "0.01", min: "0" }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Fat (g)"
                      value={formData.fat}
                      onChange={(e) => handleInputChange("fat", e.target.value)}
                      InputProps={{
                        inputProps: { step: "0.01", min: "0" }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Carbs (g)"
                      value={formData.carbs}
                      onChange={(e) => handleInputChange("carbs", e.target.value)}
                      InputProps={{
                        inputProps: { step: "0.01", min: "0" }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Glycemic Index"
                      value={formData.glycemicIndex}
                      onChange={(e) => handleInputChange("glycemicIndex", e.target.value)}
                      InputProps={{
                        inputProps: { step: "1", min: "0", max: "100" }
                      }}
                      helperText="Scale of 0-100 (blood sugar impact)"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Language-Specific Information Section */}
              <Paper elevation={3} sx={{ p: 3, backgroundColor: 'white', borderRadius: 3 }}>
                <Typography variant="h6" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>
                  🌍 Language-Specific Information
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  AI will automatically translate the food name to all languages when you use "Fill with AI"
                </Typography>

                <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
                  <Tabs
                    value={currentTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      '& .MuiTab-root': {
                        minHeight: '48px',
                        textTransform: 'none',
                        fontWeight: 500
                      }
                    }}
                  >
                    {Object.keys(formData.languageSpec).map((lang) => (
                      <Tab
                        key={lang}
                        label={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <LanguageIcon fontSize="small" />
                            {lang.charAt(0).toUpperCase() + lang.slice(1)}
                            {!defaultLanguages.includes(lang) && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveLanguage(lang);
                                }}
                                sx={{ ml: 1 }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        }
                      />
                    ))}
                    <Tab 
                      icon={<AddIcon />} 
                      label="Add Language"
                      sx={{ minWidth: '140px' }}
                    />
                  </Tabs>
                </Box>

                {Object.keys(formData.languageSpec).map((lang, index) => (
                  <Box
                    key={lang}
                    role="tabpanel"
                    hidden={currentTab !== index}
                    sx={{ py: 2 }}
                  >
                    {currentTab === index && (
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label={`Food Name in ${lang.charAt(0).toUpperCase() + lang.slice(1)}`}
                            value={formData.languageSpec[lang]?.name || ""}
                            onChange={(e) =>
                              handleLanguageChange(lang, "name", e.target.value)
                            }
                            placeholder={`Enter the food name in ${lang}`}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                '&:hover fieldset': {
                                  borderColor: 'secondary.main',
                                },
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label={`Description in ${lang.charAt(0).toUpperCase() + lang.slice(1)}`}
                            value={formData.languageSpec[lang]?.description || ""}
                            onChange={(e) =>
                              handleLanguageChange(lang, "description", e.target.value)
                            }
                            placeholder={`Enter the food description in ${lang}`}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                '&:hover fieldset': {
                                  borderColor: 'secondary.main',
                                },
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Button
                            variant="outlined"
                            component="label"
                            fullWidth
                            sx={{ 
                              height: "56px",
                              borderRadius: 2,
                              borderColor: 'secondary.main',
                              color: 'secondary.main',
                              '&:hover': {
                                borderColor: 'secondary.dark',
                                backgroundColor: 'secondary.light'
                              }
                            }}
                            disabled={formData.languageSpec[lang]?.uploading}
                          >
                            {formData.languageSpec[lang]?.uploading ? (
                              <>
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <AddIcon sx={{ mr: 1 }} />
                                Upload {lang.charAt(0).toUpperCase() + lang.slice(1)} Image
                              </>
                            )}
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={(e) => handleLanguageImageUpload(lang, e)}
                            />
                          </Button>
                        </Grid>
                        
                        {formData.languageSpec[lang]?.customImageUrl && (
                          <Grid item xs={12}>
                            <Box sx={{ 
                              border: '1px solid #e2e8f0',
                              borderRadius: 2,
                              overflow: 'hidden',
                              maxWidth: '200px'
                            }}>
                              <Typography variant="caption" sx={{ 
                                p: 1, 
                                backgroundColor: '#f8fafc', 
                                display: 'block',
                                textAlign: 'center',
                                fontWeight: 'bold'
                              }}>
                                {lang.charAt(0).toUpperCase() + lang.slice(1)} Image
                              </Typography>
                              <Box sx={{ p: 1 }}>
                                <img
                                  src={formData.languageSpec[lang].customImageUrl}
                                  alt={`${lang} preview`}
                                  style={{ 
                                    width: "100%", 
                                    maxHeight: '100px',
                                    objectFit: 'contain',
                                    borderRadius: "4px" 
                                  }}
                                />
                              </Box>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    )}
                  </Box>
                ))}
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Add Language Dialog */}
      <Dialog
        open={addLanguageDialog}
        onClose={() => setAddLanguageDialog(false)}
      >
        <DialogTitle>Add New Language</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Language Name"
            fullWidth
            value={newLanguageName}
            onChange={(e) => setNewLanguageName(e.target.value)}
            helperText="Example: Spanish, Italian, Portuguese, etc."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddLanguageDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAddLanguage}
            variant="contained"
            color="primary"
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}